import { extractTerms, expandTerms } from '../../../../utils/search/fuzzy';

export const cleanPlaintext = (text: string | null | undefined): string => {
  if (!text) return '';

  return text
    .replace(/\[@MARKER\]/g, '') // Remove [@MARKER]
    .replace(/\[@DIS_[^\]]+\]/g, '') // Remove [@DIS_...] patterns
    .replace(/!\[([^\]]*)\]\([^)]*\)/g, '') // Remove markdown images: ![alt](url)
    .replace(/\n/g, ' ') // Remove newlines
    .replace(/([a-zA-Z])(\d+)(?=\s|$|[.,;:!?\-–—])/g, '$1') // Remove superscript numbers attached to words (e.g., "fingertips1" -> "fingertips", "Alerts3" -> "Alerts")
    .replace(/^[\s.,;:!?\-–—]+/, '') // Remove leading punctuation and whitespace
    .trim(); // Trim all whitespace
};

/**
 * Highlights the search term in the text by wrapping matches in <strong> tags
 * Supports both exact matches and fuzzy/semantic matches (including synonyms)
 * Case-insensitive matching, handles substrings
 */
export const highlightSearchTerm = (
  text: string,
  searchTerm: string
): string => {
  if (!searchTerm || !text) return text;

  // Extract terms and expand with synonyms for fuzzy matching
  const terms = extractTerms(searchTerm);

  // If no terms extracted (e.g., all stop words), use the original search term
  const termsToExpand =
    terms.length > 0 ? terms : [searchTerm.toLowerCase().trim()];
  const expandedTerms = expandTerms(termsToExpand, searchTerm);

  // Also include the original search term for exact matching (normalized and original case)
  const normalizedSearchTerm = searchTerm.toLowerCase().trim();
  const allTerms = [
    searchTerm, // Original with original case
    normalizedSearchTerm, // Normalized version
    ...expandedTerms,
  ];

  // Remove duplicates and sort by length (longest first) to handle multi-word matches correctly
  // This ensures "parking brake" is matched before just "brake"
  const uniqueTerms = Array.from(new Set(allTerms))
    .filter((term) => term && term.trim().length > 0)
    .sort((a, b) => b.length - a.length);

  // Track positions that are already highlighted to avoid nested/overlapping highlights
  const highlightedPositions = new Set<number>();
  const matches: Array<{ start: number; end: number; text: string }> = [];

  // Find all matches for all terms
  for (const term of uniqueTerms) {
    if (!term || term.trim().length === 0) continue;

    // Escape special regex characters in term
    const escapedTerm = term.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

    // Create regex for case-insensitive matching (substring matching)
    const regex = new RegExp(escapedTerm, 'gi');

    let match;
    // Reset regex lastIndex to avoid issues with global regex
    regex.lastIndex = 0;

    while ((match = regex.exec(text)) !== null) {
      const start = match.index;
      const end = start + match[0].length;

      // Check if this range is already covered by a longer match
      let isOverlapped = false;
      for (let i = start; i < end; i++) {
        if (highlightedPositions.has(i)) {
          isOverlapped = true;
          break;
        }
      }

      if (!isOverlapped) {
        matches.push({ start, end, text: match[0] });
        // Mark all positions in this range as highlighted
        for (let i = start; i < end; i++) {
          highlightedPositions.add(i);
        }
      }

      // Prevent infinite loops with zero-length matches
      if (match[0].length === 0) {
        regex.lastIndex++;
      }
    }
  }

  // Sort matches by position (end to start) so we can apply highlights without affecting indices
  matches.sort((a, b) => b.start - a.start);

  // Apply highlights
  let result = text;
  for (const match of matches) {
    const before = result.substring(0, match.start);
    const matched = result.substring(match.start, match.end);
    const after = result.substring(match.end);
    result = before + '<strong>' + matched + '</strong>' + after;
  }

  return result;
};

interface PaginationConfig {
  maxVisibleItems?: number; // Number of page numbers to show around current page (default: 3)
  showEllipsis?: boolean; // Show ellipsis when pages are skipped (default: true)
}

// Helper function to calculate which page numbers to show
export const getPageNumbers = (
  currentPage: number,
  totalPages: number,
  config: PaginationConfig = {}
): (number | 'ellipsis')[] => {
  const { maxVisibleItems = 3, showEllipsis = true } = config;

  if (totalPages <= 1) return [];

  // If showEllipsis is false, show only maxVisibleItems pages around current page (no ellipsis, no first/last)
  if (!showEllipsis) {
    const pages: number[] = [];
    const halfVisible = Math.floor(maxVisibleItems / 2);

    // Calculate range centered around current page
    let rangeStart = currentPage - halfVisible;
    let rangeEnd = currentPage + (maxVisibleItems - halfVisible - 1);

    // Constrain to valid range
    rangeStart = Math.max(1, rangeStart);
    rangeEnd = Math.min(totalPages, rangeEnd);

    // Adjust if range is too small
    const currentRangeSize = rangeEnd - rangeStart + 1;
    if (currentRangeSize < maxVisibleItems) {
      const needed = maxVisibleItems - currentRangeSize;
      if (rangeStart === 1) {
        rangeEnd = Math.min(totalPages, rangeEnd + needed);
      } else if (rangeEnd === totalPages) {
        rangeStart = Math.max(1, rangeStart - needed);
      }
    }

    // Add pages in range
    for (let i = rangeStart; i <= rangeEnd; i++) {
      pages.push(i);
    }

    return pages;
  }

  // If we can show all pages without ellipsis, do that
  const maxPagesToShow = maxVisibleItems + 2; // +2 for first and last
  if (totalPages <= maxPagesToShow) {
    const pages: number[] = [];
    for (let i = 1; i <= totalPages; i++) {
      pages.push(i);
    }
    return pages;
  }

  const pages: (number | 'ellipsis')[] = [];

  // Calculate the range of pages to show around current page
  // We want exactly maxVisibleItems pages, centered around currentPage when possible
  const halfVisible = Math.floor(maxVisibleItems / 2);

  // Calculate ideal range centered around current page
  let rangeStart = currentPage - halfVisible;
  let rangeEnd = currentPage + (maxVisibleItems - halfVisible - 1);

  // Constrain to valid range (excluding page 1 and last page which are always shown)
  rangeStart = Math.max(2, rangeStart);
  rangeEnd = Math.min(totalPages - 1, rangeEnd);

  // Ensure we have exactly maxVisibleItems pages
  const currentRangeSize = rangeEnd - rangeStart + 1;
  if (currentRangeSize < maxVisibleItems) {
    // Need to expand - try to expand on both sides equally
    const needed = maxVisibleItems - currentRangeSize;
    const canExpandLeft = rangeStart > 2;
    const canExpandRight = rangeEnd < totalPages - 1;

    if (canExpandLeft && canExpandRight) {
      // Expand both sides
      const leftExpand = Math.min(Math.floor(needed / 2), rangeStart - 2);
      const rightExpand = needed - leftExpand;
      rangeStart -= leftExpand;
      rangeEnd = Math.min(totalPages - 1, rangeEnd + rightExpand);
    } else if (canExpandRight) {
      // Only expand right
      rangeEnd = Math.min(totalPages - 1, rangeEnd + needed);
    } else if (canExpandLeft) {
      // Only expand left
      rangeStart = Math.max(2, rangeStart - needed);
    }
  } else if (currentRangeSize > maxVisibleItems) {
    // Need to shrink - remove from both sides equally
    const excess = currentRangeSize - maxVisibleItems;
    const leftShrink = Math.floor(excess / 2);
    const rightShrink = excess - leftShrink;
    rangeStart += leftShrink;
    rangeEnd -= rightShrink;
  }

  // Always add first page
  pages.push(1);

  // Add ellipsis at start if there's a gap
  if (rangeStart > 2) {
    pages.push('ellipsis');
  }

  // Add pages in the range
  for (let i = rangeStart; i <= rangeEnd; i++) {
    pages.push(i);
  }

  // Add ellipsis at end if there's a gap
  if (rangeEnd < totalPages - 1) {
    pages.push('ellipsis');
  }

  // Always add last page
  pages.push(totalPages);

  return pages;
};
